import React, { useEffect, useRef, useState } from 'react';
import { Button } from '$partials/ui';
import { ArrowRightIcon } from '$partials/icon';
import { Image } from '$partials/media';
import { formatLinkBlockToUrl } from '$utilities/format';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @type {import('$types/components/partials/modules.d.ts').OffMarketBanner}
 */
const OffMarketBanner = ({
    label,
    title,
    content,
    total,
    value,
    cta,
    areaFilterOptions,
    parentPageType,
    rentTotal,
    saleTotal,
}) => {
    const text = title.replace(/\[1\]/g, saleTotal).replace(/\[2\]/g, rentTotal).replace(/\[3\]/g, total);

    const [videoHeight, setVideoHeight] = useState(undefined);
    const [videoWidth, setVideoWidth] = useState(undefined);
    const videoRef = useRef();
    let formatter = Intl.NumberFormat('en-US', { notation: 'compact', style: 'currency', currency: 'GBP'  });
    let portfolioValue = formatter.format(value).replace('B', ' bn');

    const handleVideoLoad = () => {
        setVideoHeight(videoRef.current.videoHeight);
        setVideoWidth(videoRef.current.videoWidth);
    };

    const contentObj = content?.[0];
    const isMedia = contentObj?.image || contentObj?.video;

    const isLandscape = () => {
        if (contentObj?.video) {
            return videoWidth > videoHeight;
        }
        return contentObj?.image.width > contentObj?.image.height;

    };

    useEffect(() => {
        const video = videoRef.current;

        if (video) {
            video.addEventListener('loadedmetadata', handleVideoLoad, { passive: true });

            window.setTimeout(() => {
                handleVideoLoad();
            }, 100);
        }

        return () => {
            if (video) {
                video.removeEventListener('loadedmetadata', handleVideoLoad, { passive: true });
            }
        };
    }, []);

    const getParams = (link) => {
        const searchParams = new URLSearchParams(window?.location.search);
        const areas = searchParams.getAll('areas');
        const maxPrice = searchParams.get('maxPrice');
        const minPrice = searchParams.get('minPrice');
        const filteredAreas = areaFilterOptions?.filter(item => areas?.includes(item.value))?.map(item => item.value);

        const params = `type=${parentPageType === 'RentalPropertyListingPage' ? 'rent' : 'sale'}&minPrice=${minPrice ?? ''}&maxPrice=${maxPrice ?? ''}&areas=${filteredAreas?.join('&areas=')}`;
        window.location.href=formatLinkBlockToUrl(link, params);
    };

    return (
        <>
            <div className='absolute trunk-y-bleed translate-yn-100 zn1'>
                <div className='bg-peach height-7' />
            </div>
            <div className='bg-peach trunk-full-width black'>
                <div className={`${isMedia ? 'flex-col-reverse' : ''} container col-12 flex flex-wrap sm-flex-no-wrap sm-flex-row pt6 pb9 md-py9`}>
                    <div className={`${isMedia ? '' : 'mb7'} col-12 sm-col-6 md-mb0`}>
                        <div className='col-12 md-col-10'>
                            {label && <p className='type-style-5 mb3'>{label}</p>}
                            <h2 className='type-style-1 sans-serif black'>{text}</h2>
                        </div>
                        {isMedia && (
                            <div className='col-12 md-col-8 sm-pr7 mt4 md-pr6'>
                                {cta.map((item, i) => {
                                    const link = formatLinkBlockToUrl(item.link);
                                    return (
                                        <Button
                                            as='a'
                                            className = 'bg-none type-style-5 link-reset'
                                            href={link}
                                            key={i}
                                            look='underline'
                                            renderIcon={<ArrowRightIcon/>}
                                            target={item.link?.new_window || item.link?.linkTo === 'file' ? '_blank' : ''}>
                                            {item.label}
                                        </Button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className='hidden sm-flex sm-col-1'/>
                    {isMedia ? (
                        <div className={`col-12 mb6 sm-mb0 sm-pl9 md-pl0 md-pr12${isLandscape() ? ' sm-col-7 md-col-6' : ' sm-col-6 md-col-5'}`}>
                            <div style={{maxWidth: isLandscape() ? '100%' : '500px'}}>
                                {contentObj?.video ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        ref={videoRef}
                                        src={contentObj?.video.videoUrl}/>
                                ) : (
                                    <Image {...contentObj?.image}/>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-col col-12 sm-col-4'>
                            {/* Sale Property Total Properties */}
                            {contentObj?.totalPropertiesLabel && (
                                <div className={`flex flex-col ${contentObj.totalPropertiesLabel ? 'mb5' : ''}`}>
                                    <p>
                                        {contentObj.totalPropertiesLabel}
                                    </p>
                                    <div className='type-style-2 md-type-style-1'>
                                        {saleTotal}
                                    </div>
                                </div>
                            )}
                            {/* Sale Property Portfolio Value */}
                            {contentObj?.portfolioValueLabel && (
                                <div className='flex flex-col'>
                                    <p>
                                        {contentObj.portfolioValueLabel}
                                    </p>
                                    <div className='type-style-2 md-type-style-1'>
                                        {portfolioValue}
                                    </div>
                                </div>
                            )}

                            {/* Rent Property Total Properties */}
                            {contentObj?.rentTotalPropertiesLabel && (
                                <div className={`flex flex-col ${contentObj.rentTotalPropertiesLabel ? 'mb5' : ''}`}>
                                    <p>
                                        {contentObj.rentTotalPropertiesLabel}
                                    </p>
                                    <div className='type-style-2 md-type-style-1'>
                                        {rentTotal}
                                    </div>
                                </div>
                            )}
                            {/* Rent Property Portfolio Value */}
                            {contentObj?.rentPortfolioValueLabel && (
                                <div className={`flex flex-col ${contentObj.rentPortfolioValueLabel ? 'mb5' : ''}`}>
                                    <p>
                                        {contentObj.rentPortfolioValueLabel}
                                    </p>
                                    <div className='type-style-2 md-type-style-1'>
                                        {contentObj.rentPortfolioValueTitle}
                                    </div>
                                </div>
                            )}
                            <div className='md-pr6 mt3 md-mt5 width-100'>
                                {cta.map((item, i) => {
                                    const link = formatLinkBlockToUrl(item.link);
                                    if (item?.link?.linkTo === 'page') {
                                        return (
                                            <Button
                                                as='a'
                                                className = 'bg-none type-style-5 link-reset mb3 md-mb0'
                                                iconClassName='bg-sand black button border-radius-100px p3 md-p5'
                                                look='underline'
                                                onClick={() => getParams(item.link)}
                                                renderIcon={<ArrowRightIcon/>}>
                                                {item.label}
                                            </Button>);
                                    }
                                    return (
                                        <Button
                                            as='a'
                                            className = 'bg-none type-style-5 link-reset'
                                            href={link}
                                            key={i}
                                            look='underline'
                                            renderIcon={<ArrowRightIcon/>}
                                            target={item.link?.new_window || item.link?.linkTo === 'file' ? '_blank' : ''}>
                                            {item.label}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className='absolute trunk-y-bleed zn1'>
                <div className='bg-peach height-7' />
            </div>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

export default OffMarketBanner;
